import Bugsnag from "@bugsnag/js";
import hgToast from "@/common/lib/hgToast";

type BugsnagOptions = {
  // Bugsnag severity ("info" | "warning" | "error"). Defaults to "error".
  severity?: "info" | "warning" | "error";
  // Sets event.context — useful for filtering on the Bugsnag dashboard.
  context?: string;
  // Stable string that collapses related errors into a single Bugsnag issue
  // instead of grouping by stack trace.
  groupingHash?: string;
};

/**
 * Error handler to be called in catch blocks with an Error object.
 *
 * ```
 *  import errorHandler from "@/common/lib/errorHandler"
 *  [...]
 *  try {
 *   somethingThatFails();
 *  } catch((err) => {
 *    // Call errorHandler and have it show user a custom message.
 *    errorHandler(err, false, true, true, "An error occurred");
 *  })
 * ```
 * @see https://docs.bugsnag.com/platforms/javascript/react/#reporting-handled-errors
 *
 * @param {Object} e Error object
 * @param {boolean} report Whether to report to Bugsnag
 * @param {boolean} showMessageOnError If true, an hgToast is shown to user
 * @param {string|null} errorMessage Error message shown to user, if any
 * @param {any} debugging Arbitrary debug data to send to Bugsnag
 * @param {BugsnagOptions} bugsnagOptions Optional Bugsnag tagging — severity,
 *   context, groupingHash. Use when an error needs to stand out on the Bugsnag
 *   dashboard (e.g. owned by a specific team).
 *
 */
const errorHandler = (
  e: any,
  report: boolean = true,
  showMessageOnError: boolean = true,
  errorMessage: string | null = null,
  debugging: any = null,
  bugsnagOptions: BugsnagOptions = {},
): void => {
  let msg = errorMessage || "An error occurred. Please reload the page to try again.";
  if (report) {
    Bugsnag.notify(e, (event) => {
      if (debugging) {
        event.addMetadata("debugging", debugging);
      }
      if (bugsnagOptions.severity) {
        event.severity = bugsnagOptions.severity;
      }
      if (bugsnagOptions.context) {
        event.context = bugsnagOptions.context;
      }
      if (bugsnagOptions.groupingHash) {
        event.groupingHash = bugsnagOptions.groupingHash;
      }
    });
  }
  console.error(msg, e);
  if (showMessageOnError) {
    hgToast(msg, "error");
  }
};

export default errorHandler;
